import React, { useState } from "react";
import { Link } from "react-router-dom";
import EventService from "../../services/EventService";
import EventShell, { EventError } from "./EventShell";

const AUDIENCES = [
  { value: "SENIORS", label: "Older adults" },
  { value: "PARENTS", label: "Parents & families" },
  { value: "SMB", label: "Small business / employees" },
  { value: "FAITH_COMMUNITY", label: "Faith or community group" },
  { value: "GENERAL", label: "General public" },
];

/**
 * "Invite KinShield to speak" lead form at /events/host-request.
 *
 * Usually the highest-intent page on an event site: the visitor is not looking
 * for a session, they are offering an audience.
 */
const HostRequest = () => {
  const [form, setForm] = useState({
    organization: "",
    contactName: "",
    email: "",
    phone: "",
    audience: "",
    estimatedAttendees: "",
    preferredDates: "",
    format: "IN_PERSON",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const setField = (name) => (e) =>
    setForm((prev) => ({ ...prev, [name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (
      !form.organization.trim() ||
      !form.contactName.trim() ||
      !form.email.trim()
    ) {
      setError("Please fill in your organization, name, and email.");
      return;
    }

    setSubmitting(true);
    const result = await EventService.submitHostRequest({
      ...form,
      estimatedAttendees: form.estimatedAttendees
        ? Number(form.estimatedAttendees)
        : null,
    });
    setSubmitting(false);

    if (result.success) {
      setSubmitted(true);
    } else {
      setError(
        result.message || "We couldn't submit your request. Please try again.",
      );
    }
  };

  if (submitted) {
    return (
      <EventShell narrow>
        <h1 className="ev-h1">Thank you</h1>
        <p className="ev-lead">
          We've received your request and will be in touch within a few business
          days.
        </p>
        <Link className="ev-btn ev-btn-secondary" to="/events">
          Browse upcoming sessions
        </Link>
      </EventShell>
    );
  }

  return (
    <EventShell
      narrow
      crumbs={[
        { label: "Events", to: "/events" },
        { label: "Request a speaker" },
      ]}
    >
      <h1 className="ev-h1">Invite us to speak</h1>
      <p className="ev-lead">
        We run free scam-prevention sessions for libraries, senior centers,
        faith communities, HOAs, and workplaces. Tell us about your group and
        we'll find a date.
      </p>

      {error && <EventError message={error} />}

      <form onSubmit={handleSubmit} noValidate>
        <div className="ev-panel">
          <div className="ev-field">
            <label htmlFor="hr-org">
              Organization <span className="ev-required">*</span>
            </label>
            <input
              id="hr-org"
              type="text"
              required
              value={form.organization}
              onChange={setField("organization")}
            />
          </div>

          <div className="ev-field-row">
            <div className="ev-field">
              <label htmlFor="hr-name">
                Your name <span className="ev-required">*</span>
              </label>
              <input
                id="hr-name"
                type="text"
                required
                value={form.contactName}
                onChange={setField("contactName")}
                autoComplete="name"
              />
            </div>
            <div className="ev-field">
              <label htmlFor="hr-email">
                Email <span className="ev-required">*</span>
              </label>
              <input
                id="hr-email"
                type="email"
                required
                value={form.email}
                onChange={setField("email")}
                autoComplete="email"
              />
            </div>
          </div>

          <div className="ev-field">
            <label htmlFor="hr-phone">Phone (optional)</label>
            <input
              id="hr-phone"
              type="tel"
              value={form.phone}
              onChange={setField("phone")}
              autoComplete="tel"
            />
          </div>

          <div className="ev-field">
            <label htmlFor="hr-audience">Who is the audience?</label>
            <select
              id="hr-audience"
              value={form.audience}
              onChange={setField("audience")}
            >
              <option value="">Select one</option>
              {AUDIENCES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="ev-field-row">
            <div className="ev-field">
              <label htmlFor="hr-count">Roughly how many people?</label>
              <input
                id="hr-count"
                type="number"
                min="1"
                value={form.estimatedAttendees}
                onChange={setField("estimatedAttendees")}
              />
            </div>
            <div className="ev-field">
              <label htmlFor="hr-format">Format</label>
              <select
                id="hr-format"
                value={form.format}
                onChange={setField("format")}
              >
                <option value="IN_PERSON">In person</option>
                <option value="VIRTUAL">Online</option>
                <option value="HYBRID">Either</option>
              </select>
            </div>
          </div>

          <div className="ev-field">
            <label htmlFor="hr-dates">Preferred dates or times</label>
            <input
              id="hr-dates"
              type="text"
              placeholder="e.g. Any Tuesday morning in March"
              value={form.preferredDates}
              onChange={setField("preferredDates")}
            />
          </div>

          <div className="ev-field">
            <label htmlFor="hr-message">Anything else we should know?</label>
            <textarea
              id="hr-message"
              value={form.message}
              onChange={setField("message")}
              placeholder="Topics your group cares about, accessibility needs, AV available, parking…"
            />
          </div>
        </div>

        <div className="ev-form-actions">
          <button
            type="submit"
            className="ev-btn ev-btn-primary"
            disabled={submitting}
          >
            {submitting ? "Sending…" : "Send request"}
          </button>
          <Link className="ev-btn ev-btn-quiet" to="/events">
            Cancel
          </Link>
        </div>
      </form>
    </EventShell>
  );
};

export default HostRequest;
